import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you within 24 hours.",
    });
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'contact@oneitsolutions.com',
      description: 'Send us an email anytime',
      link: 'mailto:contact@oneitsolutions.com'
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: '+1 (469) 604-4193',
      description: 'Mon-Fri from 8am to 6pm',
      link: 'tel:+14696044193'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      content: '4100 Alpha Rd Ste 478',
      description: 'Dallas, TX 75244, USA',
      link: null
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Monday - Friday',
      description: '8:00 AM - 5:00 PM CST',
      link: null
    }
  ];

  const services = [
    'AI Solutions',
    'Software Development',
    'Data Science',
    'Cloud Services',
    'Cybersecurity',
    'Talent Solutions'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Get In <span className="text-[#1A3E6E]">Touch</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Ready to transform your business with AI and innovative technology? 
              Let's discuss how we can help you achieve your goals.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 border-2 hover:border-[#1A3E6E] group">
                  <CardHeader>
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#1A3E6E] transition-colors">
                      <IconComponent className="text-[#1A3E6E] group-hover:text-white transition-colors" size={24} />
                    </div>
                    <CardTitle className="text-lg">{method.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {method.link ? (
                      <a
                        href={method.link}
                        className="text-gray-900 hover:text-[#1A3E6E] transition-colors font-semibold block mb-1"
                      >
                        {method.content}
                      </a>
                    ) : (
                      <p className="text-gray-900 font-semibold mb-1">{method.content}</p>
                    )}
                    <CardDescription className="text-sm">
                      {method.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left Side - Info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Let's Start a Conversation
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  Whether you need AI solutions, software development, or top-tier tech talent, 
                  we're here to help. Fill out the form and our team will reach out within 24 hours.
                </p>
              </div>

              <Card className="bg-gradient-to-br from-[#1A3E6E] to-[#1A3E6E] text-white border-0">
                <CardContent className="p-6">
                  <MessageSquare className="mb-4" size={32} />
                  <h3 className="text-xl font-bold mb-2">Quick Response Time</h3>
                  <p className="text-white/90">
                    We typically respond to all inquiries within 2-4 hours during business hours.
                  </p>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <h3 className="font-bold text-gray-900">What Happens Next?</h3>
                <div className="space-y-2">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#1A3E6E] rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">1</div>
                    <p className="text-gray-600 text-sm">We'll review your inquiry and assign the right expert</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#1A3E6E] rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">2</div>
                    <p className="text-gray-600 text-sm">Schedule a consultation to discuss your needs</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#1A3E6E] rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">3</div>
                    <p className="text-gray-600 text-sm">Receive a tailored proposal with solutions and timeline</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="lg:col-span-3">
              <Card className="shadow-xl border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you as soon as possible.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium text-gray-700">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="border-gray-300 focus:border-[#1A3E6E] focus:ring-[#1A3E6E]"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium text-gray-700">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          className="border-gray-300 focus:border-[#1A3E6E] focus:ring-[#1A3E6E]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="company" className="text-sm font-medium text-gray-700">
                          Company Name
                        </label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="border-gray-300 focus:border-[#1A3E6E] focus:ring-[#1A3E6E]"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (469) 604-4193"
                          className="border-gray-300 focus:border-[#1A3E6E] focus:ring-[#1A3E6E]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="service" className="text-sm font-medium text-gray-700">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1A3E6E] focus:border-[#1A3E6E]"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-medium text-gray-700">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project or inquiry..."
                        rows={6}
                        className="border-gray-300 focus:border-[#1A3E6E] focus:ring-[#1A3E6E]"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#1A3E6E] hover:bg-[#1A3E6E] text-white py-6 text-lg rounded-md transition-colors duration-200 flex items-center justify-center"
                    >
                      Send Message
                      <Send className="ml-2" size={20} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Visit Our Office</h2>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <iframe
              src="https://maps.google.com/maps?q=4100%20Alpha%20Rd%20Suite%20478%20Dallas%20TX%2075244&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="OneIT Solutions Office Location - Dallas, TX"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
